import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Loader2,
  AlertTriangle,
  Clock,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import { launchpadApi } from "@/lib/api";
import useRefreshOnFocus from "@/lib/useRefreshOnFocus";
import { initials, colorFor } from "@/lib/avatar";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

// TeamRosterWidget — Jul 2026. Manager-only Launchpad view.
// Row click opens a right-side sheet with that person's at-risk tasks
// (same shape as the At-Risk widget, filtered to one assignee) — turns
// the roster into a 1:1-review starter kit.

function formatRelative(iso) {
  if (!iso) return null;
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return null;
  const diff = Math.max(0, Date.now() - dt.getTime());
  const day = 86400000;
  if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
  if (diff < day) return `${Math.round(diff / 3600000)}h ago`;
  if (diff < 7 * day) return `${Math.round(diff / day)}d ago`;
  if (diff < 30 * day) return `${Math.round(diff / day / 7)}w ago`;
  const m = Math.round(diff / day / 30);
  return `${m}mo ago`;
}

function roleTint(role) {
  switch (role) {
    case "admin":
      return "text-rose-400 border-rose-500/30 bg-rose-500/10";
    case "manager":
      return "text-sky-400 border-sky-500/30 bg-sky-500/10";
    case "editor":
      return "text-amber-400 border-amber-500/30 bg-amber-500/10";
    default:
      return "text-zinc-400 border-zinc-700 bg-zinc-900/40";
  }
}

// Compact drill-down panel — fetches assignee-filtered at-risk on open.
function AssigneeDrillPanel({ user, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !user?.user_id) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    launchpadApi
      .getAtRisk(50, user.user_id)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setData({ tasks: [], late_count: 0, due_soon_count: 0 });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, user?.user_id]);

  const tasks = data?.tasks || [];
  const lateCount = data?.late_count || 0;
  const soonCount = data?.due_soon_count || 0;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-zinc-950 border-zinc-800 text-zinc-100 overflow-y-auto"
        data-testid="roster-drill-panel"
      >
        <SheetHeader className="text-left space-y-3 pb-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            {user && (
              <span
                className="inline-flex items-center justify-center w-10 h-10 rounded-full text-xs font-mono font-bold text-zinc-950 shrink-0"
                style={{ backgroundColor: colorFor(user.user_id) }}
              >
                {initials(user.name || user.email || "?")}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <SheetTitle className="font-heading text-lg font-light text-zinc-100 truncate">
                {user?.name || "—"}
              </SheetTitle>
              <SheetDescription className="text-xs text-zinc-500 truncate">
                {user?.email}
                {user?.role && (
                  <span
                    className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wide border ${roleTint(user.role)}`}
                  >
                    {user.role}
                  </span>
                )}
              </SheetDescription>
            </div>
          </div>
          {!loading && data && (
            <div className="flex items-center gap-3 text-[11px] font-mono">
              <span
                className={`px-2 py-1 rounded-sm border ${
                  lateCount > 0
                    ? "text-rose-300 border-rose-500/40 bg-rose-500/10"
                    : "text-zinc-500 border-zinc-800 bg-zinc-900/40"
                }`}
              >
                LATE · {lateCount}
              </span>
              <span
                className={`px-2 py-1 rounded-sm border ${
                  soonCount > 0
                    ? "text-amber-300 border-amber-500/40 bg-amber-500/10"
                    : "text-zinc-500 border-zinc-800 bg-zinc-900/40"
                }`}
              >
                DUE SOON · {soonCount}
              </span>
              {user?.scheduled_hours > 0 && (
                <span className="px-2 py-1 rounded-sm border text-zinc-300 border-zinc-700 bg-zinc-900/60 ml-auto">
                  <Clock className="inline w-3 h-3 mr-1" />
                  {Math.round(user.scheduled_hours)}h wk
                </span>
              )}
            </div>
          )}
        </SheetHeader>

        <div className="mt-4">
          {loading && (
            <div
              data-testid="roster-drill-loading"
              className="flex items-center gap-2 text-zinc-500 text-sm py-6 justify-center"
            >
              <Loader2 className="w-4 h-4 animate-spin" /> Loading tasks…
            </div>
          )}
          {!loading && tasks.length === 0 && (
            <div
              data-testid="roster-drill-empty"
              className="text-center py-8 border border-dashed border-zinc-800 rounded-sm"
            >
              <div className="text-sm text-zinc-300 mb-1">Nothing at risk.</div>
              <div className="text-xs text-zinc-500">
                {user?.name?.split(" ")[0] || "This teammate"} is clear for the next 48 hours.
              </div>
            </div>
          )}
          {!loading && tasks.length > 0 && (
            <ul className="space-y-1" data-testid="roster-drill-tasks">
              {tasks.map((t) => {
                const isLate = t.status === "late";
                const offsetLabel = isLate
                  ? `${Math.abs(t.days_offset)}d late`
                  : t.days_offset === 0
                  ? "due today"
                  : `${t.days_offset}d left`;
                return (
                  <li
                    key={t.task_id}
                    data-testid={`roster-drill-task-${t.task_id}`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        // Close first, then navigate on the next tick so
                        // the Sheet fully unmounts before the route change.
                        // Chrome-on-iPad specifically stranded the Radix
                        // portal/overlay when close + nav fired in the
                        // same event → blank screen.
                        onClose();
                        setTimeout(
                          () =>
                            navigate(
                              `/projects/${t.project_id}/plan?task=${t.task_id}`
                            ),
                          50,
                        );
                      }}
                      className="w-full text-left flex items-center gap-2.5 py-2 px-2 rounded-sm hover:bg-zinc-900/60 transition-colors group"
                    >
                      <div
                        className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                          isLate ? "bg-rose-400" : "bg-amber-400"
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-200 truncate leading-tight">
                          {t.task_name}
                        </div>
                        <div className="text-[10px] font-mono text-zinc-600 truncate mt-0.5">
                          {t.project_name} · {t.phase || "—"}
                        </div>
                      </div>
                      <span
                        className={`text-[10px] font-mono uppercase tracking-wide shrink-0 ${
                          isLate ? "text-rose-300" : "text-amber-300"
                        }`}
                      >
                        {offsetLabel}
                      </span>
                      <ExternalLink className="w-3 h-3 text-zinc-700 group-hover:text-amber-400 transition-colors shrink-0" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function TeamRosterWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillUser, setDrillUser] = useState(null);

  useRefreshOnFocus(() => {
    let cancelled = false;
    launchpadApi
      .getTeamRoster()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <article
        data-testid="team-roster-loading"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5 flex items-center gap-2 text-zinc-500 text-sm"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading team roster…
      </article>
    );
  }

  if (!data || (data.users || []).length === 0) return null;

  const users = data.users || [];
  const flagged = users.filter((u) => u.at_risk_count > 0).length;

  return (
    <>
      <article
        data-testid="team-roster-widget"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
        style={{ borderLeftColor: "#8b5cf6", borderLeftWidth: 3 }}
      >
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-overline text-violet-400/80 mb-1 flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Team Roster · Manager View
            </div>
            <h2
              data-testid="team-roster-headline"
              className="font-heading text-xl font-light text-zinc-100 leading-tight"
            >
              {users.length}{" "}
              {users.length === 1 ? "teammate" : "teammates"} across your projects
              {flagged > 0 && (
                <>
                  {" · "}
                  <span className="text-rose-300">
                    {flagged} need{flagged === 1 ? "s" : ""} attention
                  </span>
                </>
              )}
              .
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              Click a row to drill into that person&apos;s at-risk tasks — great for 1:1s.
            </p>
          </div>
        </div>

        <ul className="space-y-1" data-testid="team-roster-list">
          {users.map((u) => {
            const rt = roleTint(u.role);
            const lastAgo = formatRelative(u.last_active_at);
            const hasHours = u.scheduled_hours > 0;
            return (
              <li key={u.user_id}>
                <button
                  type="button"
                  onClick={() => setDrillUser(u)}
                  data-testid={`team-roster-row-${u.user_id}`}
                  title={`Drill into ${u.name}'s at-risk tasks`}
                  className="w-full text-left flex items-start sm:items-center gap-3 py-2 pl-2 pr-3 rounded-sm hover:bg-zinc-900/60 transition-colors group"
                >
                  <span
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[11px] font-mono font-bold text-zinc-950 shrink-0"
                    style={{ backgroundColor: colorFor(u.user_id) }}
                  >
                    {initials(u.name || u.email || "?")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-zinc-100 truncate">
                        {u.name}
                      </span>
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wide border shrink-0 ${rt}`}
                      >
                        {u.role}
                      </span>
                    </div>
                    <div className="text-[10px] text-zinc-600 font-mono truncate">
                      {u.shared_project_count} shared project
                      {u.shared_project_count === 1 ? "" : "s"}
                      {lastAgo ? ` · last active ${lastAgo}` : ""}
                    </div>
                    {/* Mobile-only metrics row — on <sm the badges get their
                        own row below the name so nothing is squeezed. */}
                    <div className="flex sm:hidden items-center gap-2 mt-1.5">
                      <div
                        data-testid={`team-roster-atrisk-mobile-${u.user_id}`}
                        className={`flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${
                          u.at_risk_count >= 3
                            ? "text-rose-300 border-rose-500/40 bg-rose-500/10"
                            : u.at_risk_count >= 1
                            ? "text-amber-300 border-amber-500/40 bg-amber-500/10"
                            : "text-zinc-500 border-zinc-800 bg-zinc-900/40"
                        }`}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        {u.at_risk_count} risk
                      </div>
                      <div
                        data-testid={`team-roster-hours-mobile-${u.user_id}`}
                        className={`flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${
                          hasHours
                            ? "text-zinc-200 border-zinc-700 bg-zinc-900/60"
                            : "text-zinc-600 border-zinc-800 bg-zinc-900/40"
                        }`}
                      >
                        <Clock className="w-3 h-3" />
                        {Math.round(u.scheduled_hours)}h wk
                      </div>
                    </div>
                  </div>
                  {/* Desktop-only metrics — hidden below sm. */}
                  <div
                    data-testid={`team-roster-atrisk-${u.user_id}`}
                    className={`hidden sm:flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm border shrink-0 w-20 justify-center ${
                      u.at_risk_count >= 3
                        ? "text-rose-300 border-rose-500/40 bg-rose-500/10"
                        : u.at_risk_count >= 1
                        ? "text-amber-300 border-amber-500/40 bg-amber-500/10"
                        : "text-zinc-500 border-zinc-800 bg-zinc-900/40"
                    }`}
                  >
                    <AlertTriangle className="w-3 h-3" />
                    {u.at_risk_count} risk
                  </div>
                  <div
                    data-testid={`team-roster-hours-${u.user_id}`}
                    className={`hidden sm:flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm border shrink-0 w-20 justify-center ${
                      hasHours
                        ? "text-zinc-200 border-zinc-700 bg-zinc-900/60"
                        : "text-zinc-600 border-zinc-800 bg-zinc-900/40"
                    }`}
                  >
                    <Clock className="w-3 h-3" />
                    {Math.round(u.scheduled_hours)}h wk
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-violet-400 transition-colors shrink-0 mt-1 sm:mt-0" />
                </button>
              </li>
            );
          })}
        </ul>
      </article>
      {/* Conditionally render so Radix's exit animation can't strand its
          overlay on iPad Chrome — well-documented WebKit compositor bug
          that a `pointer-events` cleanup alone doesn't fully cover. The
          Sheet still animates *in* thanks to its opening transition; on
          close we drop the whole tree instead of animating out. */}
      {drillUser && (
        <AssigneeDrillPanel
          user={drillUser}
          open={!!drillUser}
          onClose={() => setDrillUser(null)}
        />
      )}
    </>
  );
}
